import i18n from '@/i18n/config';
import type { PayoutDestination, PayoutDestinationType } from '../types/payout.types';

/**
 * Affichage d'une `PayoutDestination` — remplace la fiction `PayoutMethod`
 * (voir `payout.types.ts`). Le serveur renvoie 5 types réels
 * (`IBAN | MOBILE_MONEY | STRIPE_CONNECT_ACCOUNT | ADYEN_ACCOUNT |
 * MOLLIE_CONNECTOR`), jamais `BANK_TRANSFER`, avec des `details` DÉJÀ
 * masqués côté serveur (`{ iban: "FR14****2606" }`).
 *
 * Même table de correspondance que pay-saas
 * (`pay/volovolo-pay-saas/app/lib/sellers/payout-provider-display.ts`), pour
 * que les deux produits s'accordent : IBAN et MOBILE_MONEY sont des rails de
 * virement (libellé texte), STRIPE_CONNECT_ACCOUNT/ADYEN_ACCOUNT sont des
 * PSP réels (libellé = nom du PSP). `MOLLIE_CONNECTOR` est réservé et n'a
 * pas de libellé dédié : il retombe, comme tout type non reconnu, sur un
 * libellé neutre — jamais « Mobile Money ».
 */

const LABEL_KEY_BY_TYPE: Partial<Record<PayoutDestinationType, string>> = {
  IBAN: 'payouts.destinations.type.IBAN',
  MOBILE_MONEY: 'payouts.destinations.type.MOBILE_MONEY',
  STRIPE_CONNECT_ACCOUNT: 'payouts.destinations.type.STRIPE_CONNECT_ACCOUNT',
  ADYEN_ACCOUNT: 'payouts.destinations.type.ADYEN_ACCOUNT',
};

/** Libellé fidèle au type réel. Jamais « Mobile Money » pour un type non reconnu. */
export function payoutDestinationLabel(destination: PayoutDestination): string {
  const key = LABEL_KEY_BY_TYPE[destination.type];
  return key ? i18n.t(key) : i18n.t('payouts.destinations.unknownLabel');
}

/**
 * Clé du champ `details` masqué à afficher selon le type. `details` est
 * déjà masqué par le serveur — on le lit tel quel, on ne le re-masque JAMAIS.
 */
const DETAILS_KEY_BY_TYPE: Partial<Record<PayoutDestinationType, string>> = {
  IBAN: 'iban',
  MOBILE_MONEY: 'msisdn',
};

/**
 * Info compte lisible, lue depuis `details` (déjà masqué par le serveur).
 * Retombe sur la première valeur présente si le type n'a pas de clé connue
 * (ex. comptes PSP) ; retombe sur '' plutôt que d'inventer une info fausse
 * quand `details` est vide.
 */
export function payoutDestinationAccountInfo(destination: PayoutDestination): string {
  const key = DETAILS_KEY_BY_TYPE[destination.type];
  const knownValue = key ? destination.details[key] : undefined;
  if (knownValue) return knownValue;

  const firstValue = Object.values(destination.details).find(Boolean);
  return firstValue ?? '';
}

export type PayoutDestinationIconKind = 'mobile' | 'bank' | 'psp' | 'generic';

const ICON_KIND_BY_TYPE: Partial<Record<PayoutDestinationType, PayoutDestinationIconKind>> = {
  IBAN: 'bank',
  MOBILE_MONEY: 'mobile',
  STRIPE_CONNECT_ACCOUNT: 'psp',
  ADYEN_ACCOUNT: 'psp',
  // MOLLIE_CONNECTOR volontairement absent : réservé, non mappé côté
  // pay-saas non plus (payout-provider-display.ts) — retombe sur 'generic'.
};

/**
 * Choix d'icône. `'mobile'` UNIQUEMENT pour `MOBILE_MONEY` — c'est le cœur
 * du bug corrigé ici (l'icône téléphone s'affichait pour tout le monde).
 * Un type non reconnu retombe sur `'generic'`, jamais `'mobile'`.
 */
export function payoutDestinationIconKind(destination: PayoutDestination): PayoutDestinationIconKind {
  return ICON_KIND_BY_TYPE[destination.type] ?? 'generic';
}
